import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Container
} from "@mui/material";
import {
  Search,
  LayoutGrid,
  List,
  Plus,
  Calendar,
  Eye,
  Trash2,
  Edit2,
  FileImage
} from 'lucide-react';
import { tokens } from "../../theme";
import Modal from "../../utils/components/Modal";
import Cancellation from "../../utils/components/Cancellation";
import GalleryForm from "./GalleryForm";
import { deleteGallery, deleteGalleryItems, fetchGalleryItems, updateGallery } from "./GalleryServices";
import { getFileType } from "../../utils/functions/Helpers";
import MediaContent from "../../utils/components/MediaContent";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import CustomDataTable from "../../utils/components/CustomDataTable";

const Gallery = () => {
  const [galleryData, setGalleryData] = useState({ content: [], totalElements: 0 });
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [showEditionModal, setShowEditionModal] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [params, setParams] = useState({ pageSize: 12, page: 0 });
  const [itemMedia, setItemMedia] = useState(null);
  const [galleryItem, setGalleryItem] = useState(null);
  const [state, setState] = useState(false);
  const setIsLoading = useLoadingSpinner();
  const [ids, setIds] = useState([]);

  // High-Volume Optimization States
  const [viewMode, setViewMode] = useState('card');
  const [searchQuery, setSearchQuery] = useState('');

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const lunchEditionModal = (galleryRow) => {
    setGalleryItem(galleryRow);
    setShowEditionModal(true);
  };

  const lunchDeletionModal = (selectedId) => {
    setIds([selectedId]);
    setShowDeletionModal(true);
  };

  const lunchMediaModal = (selectedRow) => {
    setItemMedia(selectedRow.sharedMedia);
    setShowMedia(true);
  };

  const handleDeletion = async () => {
    setIsLoading(true);
    if (ids.length > 1) {
      await deleteGalleryItems(ids, setState, () => { }, setIsLoading);
    } else {
      await deleteGallery(ids[0], setState, () => { }, setIsLoading);
    }
    setIds([]);
    setShowDeletionModal(false);
  };

  useEffect(() => {
    setIsLoading(true);
    fetchGalleryItems(params, () => { }, setGalleryData, setIsLoading);
  }, [state, params, setIsLoading]);

  const filteredGallery = galleryData.content.filter(item =>
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      field: "title",
      headerName: "Title",
      width: 300,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap="12px">
          <Box sx={{ width: 40, height: 40, borderRadius: '8px', overflow: 'hidden', bgcolor: colors.primary[500] }}>
            {params.row.sharedMedia?.mediaUrl ? (
              <img src={params.row.sharedMedia.mediaUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : <FileImage size={20} color={colors.grey[500]} />}
          </Box>
          <Typography fontWeight="bold" color={colors.grey[100]}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: "postedTime",
      headerName: "Posted Date",
      width: 180,
      valueGetter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    {
      field: "sharedMedia",
      headerName: "Type",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getFileType(params.value?.type) || "MEDIA"}
          size="small"
          sx={{
            bgcolor: `${colors.blueAccent[500]}1A`,
            color: colors.blueAccent[300],
            fontWeight: '700'
          }}
        />
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <Box display="flex" gap="4px">
          <IconButton size="small" onClick={() => lunchEditionModal(params.row)} sx={{ color: colors.grey[100] }}>
            <Edit2 size={18} />
          </IconButton>
          <IconButton size="small" onClick={() => lunchMediaModal(params.row)} sx={{ color: colors.blueAccent[300] }}>
            <Eye size={18} />
          </IconButton>
          <IconButton size="small" onClick={() => lunchDeletionModal(params.row.id)} sx={{ color: colors.redAccent[500] }}>
            <Trash2 size={18} />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: "40px", pb: "80px" }}>
      {/* Enhanced Header Section */}
      <Box
        mb="40px"
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          padding: "32px",
          borderRadius: "24px",
          border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200]}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="24px">
          <Box>
            <Typography variant="h2" fontWeight="bold" sx={{ color: colors.grey[100], mb: "4px", fontFamily: "Josefin Sans" }}>
              Gallery Management
            </Typography>
            <Typography variant="h5" color={theme.palette.mode === 'dark' ? colors.greenAccent[500] : colors.greenAccent[300]} fontWeight="500">
              {galleryData.totalElements} visual assets in collection
            </Typography>
          </Box>

          <Box display="flex" gap="12px" alignItems="center">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              size="small"
              sx={{
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                borderRadius: '12px',
                p: '4px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.grey[300],
                  '&.Mui-selected': {
                    bgcolor: colors.greenAccent[500],
                    color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                    '&:hover': { bgcolor: colors.greenAccent[400] }
                  }
                }
              }}
            >
              <ToggleButton value="card"><LayoutGrid size={18} /></ToggleButton>
              <ToggleButton value="list"><List size={18} /></ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={() => setShowCreationModal(true)}
              sx={{
                backgroundColor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                fontWeight: "bold",
                padding: "10px 24px",
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: `0 8px 20px ${colors.greenAccent[500]}33`,
                "&:hover": {
                  backgroundColor: colors.greenAccent[400],
                  transform: "translateY(-2px)"
                },
                transition: "all 0.2s"
              }}
            >
              Add Item
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap="16px" alignItems="center">
          <TextField
            placeholder="Search gallery by title or description..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                bgcolor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "#FFFFFF",
                '& fieldset': { borderColor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200] }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color={colors.grey[300]} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Grid or List View */}
      {viewMode === 'card' ? (
        <Grid container spacing={3}>
          {filteredGallery.map((item) => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={item.id}>
              <Card sx={{
                height: "100%",
                borderRadius: "24px",
                border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200]}`,
                backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#FFFFFF",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  borderColor: colors.greenAccent[500]
                }
              }}>
                <Box sx={{ position: 'relative', pt: '60%', bgcolor: colors.primary[500], cursor: 'pointer' }} onClick={() => lunchMediaModal(item)}>
                  {item.sharedMedia?.mediaUrl ? (
                    <CardMedia
                      component={getFileType(item.sharedMedia.type) === "VIDEO" ? "video" : "img"}
                      image={item.sharedMedia.mediaUrl}
                      sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                      <FileImage size={48} color={colors.grey[500]} />
                    </Box>
                  )}
                  <Chip
                    label={getFileType(item.sharedMedia?.type) || "MEDIA"}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      fontWeight: 'bold',
                      backdropFilter: 'blur(4px)'
                    }}
                  />
                </Box>

                <CardContent sx={{ p: "20px", flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb="8px" noWrap>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[400]} sx={{ mb: "20px", flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {item.description}
                  </Typography>

                  <Box display="flex" flexDirection="column" gap="8px" mb="24px">
                    <Box display="flex" alignItems="center" gap="8px">
                      <Calendar size={14} color={colors.greenAccent[500]} />
                      <Typography variant="caption" color={colors.grey[300]}>
                        Posted: {new Date(item.postedTime).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" gap="10px">
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Edit2 size={16} />}
                      onClick={() => lunchEditionModal(item)}
                      sx={{
                        borderRadius: "10px",
                        borderColor: theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200],
                        color: colors.grey[100],
                        textTransform: "none",
                        fontWeight: "600",
                        "&:hover": { borderColor: colors.greenAccent[500], color: colors.greenAccent[500] }
                      }}
                    >
                      Edit
                    </Button>
                    <IconButton
                      onClick={() => lunchMediaModal(item)}
                      sx={{
                        bgcolor: `${colors.blueAccent[500]}1A`,
                        color: colors.blueAccent[300],
                        borderRadius: "10px",
                        "&:hover": { bgcolor: colors.blueAccent[300], color: "white" }
                      }}
                    >
                      <Eye size={18} />
                    </IconButton>
                    <IconButton
                      onClick={() => lunchDeletionModal(item.id)}
                      sx={{
                        color: colors.redAccent[500],
                        bgcolor: `${colors.redAccent[500]}1A`,
                        borderRadius: "10px",
                        "&:hover": { bgcolor: colors.redAccent[500], color: "white" }
                      }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <CustomDataTable
          addRecordButtonTitle={"Add Item"}
          headerColumn={columns}
          lunchCreateModal={() => setShowCreationModal(true)}
          paginationModel={params}
          rows={{ ...galleryData, content: filteredGallery }}
          setPaginationModel={setParams}
          tableTitle={"Gallery List"}
        />
      )}

      {/* Modals */}
      <Modal isOpen={showCreationModal} onClose={setShowCreationModal}>
        <GalleryForm setState={setState} toggleModal={setShowCreationModal} />
      </Modal>
      <Modal isOpen={showEditionModal} onClose={setShowEditionModal}>
        <GalleryForm
          gallery={galleryItem}
          updateGallery={updateGallery}
          toggleModal={setShowEditionModal}
          setState={setState}
          isUpdating={true}
        />
      </Modal>
      <Modal isOpen={showDeletionModal} onClose={setShowDeletionModal}>
        <Cancellation
          actionHandler={handleDeletion}
          actionText={"Delete"}
          cancelText={"Go Back"}
          setOpen={setShowDeletionModal}
          title={ids.length > 1 ? `Delete Items` : `Delete Item`}
          warningMessage={`Are you sure you want to delete ${ids.length > 1 ? 'these gallery items' : 'this gallery item'}? This action cannot be undone.`}
        />
      </Modal>
      <Modal isOpen={showMedia} onClose={setShowMedia}>
        {itemMedia && (
          <MediaContent
            type={getFileType(itemMedia.type)}
            src={itemMedia.mediaUrl}
            alt={itemMedia.blobName}
          />
        )}
      </Modal>
      <ToastContainer />
    </Container>
  );
};

export default Gallery;
